import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs));

export const initialsOf = (name?: string | null, email?: string | null): string => {
  const source = (name ?? email ?? "").trim();
  if (!source) return "?";
  const parts = source.split(/\s+/);
  if (parts.length === 1) return source.slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
};

export const formatRelative = (
  iso: string | null | undefined,
  now: Date = new Date()
): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = (d.getTime() - now.getTime()) / 1000;
  const abs = Math.abs(diff);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 31536000],
    ["month", 2592000],
    ["week", 604800],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
    ["second", 1],
  ];
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [unit, secs] of units) {
    if (abs >= secs || unit === "second") {
      return rtf.format(Math.round(diff / secs), unit);
    }
  }
  return "—";
};

export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const parseUserAgent = (
  ua: string | null | undefined
): { browser: string; os: string } => {
  if (!ua) return { browser: "Unknown", os: "Unknown" };
  const browsers: Array<[RegExp, string]> = [
    [/Edg\//, "Edge"],
    [/OPR\//, "Opera"],
    [/Firefox\//, "Firefox"],
    [/Chrome\//, "Chrome"],
    [/Safari\//, "Safari"],
  ];
  const oss: Array<[RegExp, string]> = [
    // Check iOS / Android before macOS / Linux — iOS UA strings include
    // "Mac OS X" and Android UAs include "Linux", so the broader checks
    // win if they run first.
    [/iPhone|iPad|iPod|iOS/, "iOS"],
    [/Android/, "Android"],
    [/Windows NT/, "Windows"],
    [/Mac OS X/, "macOS"],
    [/Linux/, "Linux"],
  ];
  return {
    browser: browsers.find(([r]) => r.test(ua))?.[1] ?? "Unknown",
    os: oss.find(([r]) => r.test(ua))?.[1] ?? "Unknown",
  };
};
